import React from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { Waves, Radio, Film, Zap, LayoutGrid, BookMarked, ChevronsUpDown, Search, Command } from 'lucide-react';
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarRail, SidebarTrigger } from './ui/Sidebar';
import { Avatar, AvatarFallback } from './ui/Avatar';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/DropdownMenu';
import { Separator } from './ui/Separator';
import { RouteBreadcrumb } from './RouteBreadcrumb';
import { freesoundCollections, podcastCollections, youtubeCollections, riveCollections, freesoundSounds, podcastEpisodes, youtubeVideos, riveFiles } from '../src/seed';
type FeatureKey = 'freesound' | 'podcast' | 'youtube' | 'rive';
interface FeatureConfig {
  key: FeatureKey;
  label: string;
  icon: React.ElementType;
  collectionCount: number;
  itemCount: number;
}
const features: FeatureConfig[] = [{
  key: 'freesound',
  label: 'Freesound',
  icon: Waves,
  collectionCount: freesoundCollections.length,
  itemCount: freesoundSounds.length
}, {
  key: 'podcast',
  label: 'Podcast',
  icon: Radio,
  collectionCount: podcastCollections.length,
  itemCount: podcastEpisodes.length
}, {
  key: 'youtube',
  label: 'YouTube',
  icon: Film,
  collectionCount: youtubeCollections.length,
  itemCount: youtubeVideos.length
}, {
  key: 'rive',
  label: 'Rive',
  icon: Zap,
  collectionCount: riveCollections.length,
  itemCount: riveFiles.length
}];
function NavRow({
  to,
  icon: Icon,
  label,
  count
}: {
  to: string;
  icon: React.ElementType;
  label: string;
  count?: number;
}) {
  const {
    pathname
  } = useLocation();
  const active = pathname.startsWith(to);
  return <SidebarMenuItem data-id="element-40">
      <SidebarMenuButton isActive={active} asChild data-id="element-41">
        <NavLink to={to} className="flex w-full items-center gap-2.5 text-[13px]" data-id="element-42">
          <Icon className="size-3.5 shrink-0 text-muted-foreground group-data-[active=true]/menu-item:text-foreground" strokeWidth={active ? 2.25 : 1.75} data-id="element-43" />
          <span className="font-medium tracking-[-0.005em]" data-id="element-44">{label}</span>
          {count !== undefined && <span className="ml-auto font-mono text-[10px] text-muted-foreground tabular-nums" data-id="element-45">
              {String(count).padStart(2, '0')}
            </span>}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>;
}
function FeatureGroup({
  feature,
  index
}: {
  feature: FeatureConfig;
  index: number;
}) {
  const {
    label,
    icon,
    collectionCount,
    itemCount,
    key
  } = feature;
  const Icon = icon;
  return <SidebarGroup className="px-2 py-0" data-id="element-46">
      <SidebarGroupLabel className="h-8 px-2 flex items-center gap-2 font-mono text-[9.5px] uppercase tracking-[0.18em] text-muted-foreground/70" data-id="element-47">
        <span className="tabular-nums text-muted-foreground/50" data-id="element-48">
          {String(index + 1).padStart(2, '0')}
        </span>
        <Icon className="size-3 shrink-0" strokeWidth={1.75} data-id="element-49" />
        <span data-id="element-50">{label}</span>
      </SidebarGroupLabel>
      <SidebarGroupContent data-id="element-51">
        <SidebarMenu data-id="element-52">
          <NavRow to={`/${key}/collections`} icon={LayoutGrid} label="Collections" count={collectionCount} data-id="element-53" />
          <NavRow to={`/${key}/library`} icon={BookMarked} label="Library" count={itemCount} data-id="element-54" />
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>;
}
export function AppShell() {
  return <SidebarProvider data-id="element-55">
      <Sidebar variant="inset" collapsible="icon" data-id="element-56">
        {/* Brand */}
        <SidebarHeader className="pb-3 pt-3" data-id="element-57">
          <SidebarMenu data-id="element-58">
            <SidebarMenuItem data-id="element-59">
              <SidebarMenuButton size="lg" asChild className="h-auto py-2 hover:bg-transparent" data-id="element-60">
                <NavLink to="/" className="flex items-center gap-3" data-id="element-61">
                  <div className="flex aspect-square size-9 items-center justify-center rounded-[10px] bg-gradient-to-br from-lime-400 via-emerald-500 to-sky-500 shadow-[inset_0_-1px_0_rgba(0,0,0,0.15),0_1px_2px_rgba(0,0,0,0.06)]" data-id="element-62">
                    <span className="font-serif text-lg font-semibold leading-none text-white" data-id="element-63">
                      D
                    </span>
                  </div>
                  <div className="flex-1 min-w-0 grid gap-1 text-left" data-id="element-64">
                    <span className="font-serif text-[17px] font-medium tracking-[-0.02em] leading-none text-foreground" data-id="element-65">
                      dhp.studio
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.22em] leading-none text-muted-foreground/80" data-id="element-66">
                      Content Studio · v0.1
                    </span>
                  </div>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        {/* Command bar (decorative) */}
        <div className="px-4 pb-3" data-id="element-67">
          <button className="w-full h-8 rounded-md border border-border/80 bg-background/40 px-2.5 flex items-center gap-2 text-muted-foreground hover:bg-background hover:text-foreground transition-colors" data-id="element-68">
            <Search className="size-3.5" strokeWidth={1.75} data-id="element-69" />
            <span className="text-[12px] font-medium tracking-[-0.005em]" data-id="element-70">
              Search
            </span>
            <span className="ml-auto flex items-center gap-0.5 font-mono text-[9.5px] text-muted-foreground/70" data-id="element-71">
              <kbd className="inline-flex items-center justify-center rounded border border-border/80 bg-background size-[18px]" data-id="element-72">
                <Command className="size-2.5" strokeWidth={2} data-id="element-73" />
              </kbd>
              <kbd className="inline-flex items-center justify-center rounded border border-border/80 bg-background h-[18px] min-w-[18px] px-1 text-[10px] font-medium" data-id="element-74">
                K
              </kbd>
            </span>
          </button>
        </div>

        <Separator className="mx-4 mb-1 w-auto bg-border/70" data-id="element-75" />

        {/* Feature groups */}
        <SidebarContent className="gap-0" data-id="element-76">
          {features.map((feature, idx) => <React.Fragment key={feature.key} data-id="element-77">
              <FeatureGroup feature={feature} index={idx} data-id="element-78" />
              {idx < features.length - 1 && <Separator className="mx-4 my-2 w-auto bg-border/50" data-id="element-79" />}
            </React.Fragment>)}
        </SidebarContent>

        {/* Editorial footer */}
        <SidebarFooter className="border-t border-border/70 p-2" data-id="element-80">
          <SidebarMenu data-id="element-81">
            <SidebarMenuItem data-id="element-82">
              <DropdownMenu data-id="element-83">
                <DropdownMenuTrigger asChild data-id="element-84">
                  <SidebarMenuButton size="lg" className="h-auto py-2 data-[state=open]:bg-sidebar-accent" data-id="element-85">
                    <Avatar className="h-8 w-8 rounded-lg border border-border/60" data-id="element-86">
                      <AvatarFallback className="rounded-lg bg-muted font-mono text-[10px] font-semibold tracking-wider" data-id="element-87">
                        MB
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left leading-tight gap-0.5" data-id="element-88">
                      <span className="truncate font-serif text-[14px] font-medium tracking-[-0.01em]" data-id="element-89">
                        Matt Bourne
                      </span>
                      <span className="truncate font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground" data-id="element-90">
                        Editor · Tiny Medical Apps
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-3.5 text-muted-foreground" data-id="element-91" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-60 rounded-lg" side="top" align="end" sideOffset={8} data-id="element-92">
                  <DropdownMenuItem data-id="element-93">Account</DropdownMenuItem>
                  <DropdownMenuItem data-id="element-94">Preferences</DropdownMenuItem>
                  <DropdownMenuItem data-id="element-95">Sign out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail data-id="element-96" />
      </Sidebar>

      <SidebarInset data-id="element-97">
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-3 bg-background/80 backdrop-blur-md px-5 border-b border-border/70" data-id="element-98">
          <SidebarTrigger className="-ml-1" data-id="element-99" />
          <Separator orientation="vertical" className="h-4" data-id="element-100" />
          <RouteBreadcrumb data-id="element-101" />
          <div className="ml-auto flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground" data-id="element-102">
            <span className="flex items-center gap-1.5" data-id="element-103">
              <span className="inline-flex size-1.5 rounded-full bg-emerald-500" data-id="element-104" />
              Connected
            </span>
            <span className="h-3 w-px bg-border" data-id="element-105" />
            <span className="tabular-nums" data-id="element-106">
              {new Date().toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            })}
            </span>
          </div>
        </header>
        <main className="flex-1 px-8 py-10" data-id="element-107">
          <Outlet data-id="element-108" />
        </main>
      </SidebarInset>
    </SidebarProvider>;
}